import axios from "api/axios";

export type JobStatus =
  | "new"
  | "reviewing"
  | "applied"
  | "interviewing"
  | "offer_received"
  | "hired"
  | "rejected"
  | "not_relevant"
  | "archived";

export interface LinkedInJob {
  id: number;
  job_posting_id: string;
  job_title: string;
  company_name: string;
  company_id?: string;
  job_location?: string;
  job_summary?: string;
  job_employment_type?: string;
  job_seniority_level?: string;
  job_function?: string;
  job_industries?: string;
  company_url?: string;
  company_logo?: string;
  url: string;
  apply_link?: string;
  country_code?: string;
  is_easy_apply: boolean;
  application_availability: boolean;
  job_num_applicants: number;
  job_posted_date?: string;
  source: string;
  remote_scope?: string | null;
  remote_worldwide_analysis?: string | null;
  llm_analysis_metadata?: string | null;
  company_can_apply?: boolean | null;
  company_analysis_metadata?: string | null;
  raw_job?: string | null;
  is_coldfusion?: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;

  // Present when loaded from /alerts endpoint
  alert_id?: number;
  status?: JobStatus;
  alert_notes?: string | null;
  alert_from_status?: string | null;
  alert_created_at?: string | null;
  alert_updated_at?: string | null;
  employee_id?: number; // admin view only

  // CV enhancement fields (present in alerts)
  has_enhancement?: boolean;
  has_enhancement_logs?: boolean;
  enhanced_at?: string | null;
  cv_id_enhanced?: number | null;
  enhanced_cv_data?: any | null; // only in alert detail response

  // Cover letter enhancement fields (present in alerts)
  has_cover_letter_enhancement?: boolean;
  has_cover_letter_enhancement_logs?: boolean;
  cover_letter_enhanced_at?: string | null;
  cover_letter_id_enhanced?: number | null;
  enhanced_cover_letter_data?: any | null; // only in alert detail response
}

export interface JobBoardPage {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: LinkedInJob[];
}

export interface TechManager {
  id: number;
  employee_code: string;
  name: string;
  email: string;
}

export interface AlertFilters {
  page?: number;
  page_size?: number;
  employee_id?: number;
  status_filter?: string;
  hide_archived?: boolean;
  employment_type?: string;
  seniority?: string;
  search?: string;
  remote_scope_filter?: string;
  company_can_apply_only?: boolean;
}

export interface AlertHistoryEntry {
  id: number;
  from_status: string | null;
  to_status: string;
  notes: string | null;
  changed_at: string | null;
}

export const STATUS_OPTIONS: { value: JobStatus; label: string }[] = [
  { value: "new",            label: "New" },
  { value: "reviewing",      label: "Reviewing" },
  { value: "applied",        label: "Applied" },
  { value: "interviewing",   label: "Interviewing" },
  { value: "offer_received", label: "Offer Received" },
  { value: "hired",          label: "Hired" },
  { value: "rejected",       label: "Rejected" },
  { value: "not_relevant",   label: "Not Relevant" },
  { value: "archived",       label: "Archived" },
];

export async function fetchAlertStatusCounts(isAdmin: boolean, employeeId?: number): Promise<Record<string, number>> {
  const base = isAdmin ? "/admin/linkedin-jobs/alerts/status-counts" : "/employee/linkedin-jobs/alerts/status-counts";
  const params = isAdmin && employeeId ? { employee_id: employeeId } : {};
  const res = await axios.get(base, { params });
  return res.data;
}

export async function fetchAlerts(filters: AlertFilters = {}, isAdmin = false): Promise<JobBoardPage> {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== "")
  );
  const base = isAdmin ? "/admin/linkedin-jobs/alerts" : "/employee/linkedin-jobs/alerts";
  const res = await axios.get(base, { params });
  return res.data;
}

export async function updateAlertStatus(
  alertId: number,
  status: JobStatus,
  notes: string | null,
  isAdmin: boolean,
): Promise<void> {
  const base = isAdmin ? "/admin/linkedin-jobs" : "/employee/linkedin-jobs";
  await axios.patch(`${base}/alerts/${alertId}/status`, { status, notes });
}

export async function fetchAlertHistory(
  alertId: number,
  isAdmin: boolean,
): Promise<AlertHistoryEntry[]> {
  const base = isAdmin ? "/admin/linkedin-jobs" : "/employee/linkedin-jobs";
  const res = await axios.get(`${base}/alerts/${alertId}/history`);
  return res.data;
}

export async function fetchAlertDetail(alertId: number, isAdmin: boolean): Promise<LinkedInJob> {
  const base = isAdmin ? "/admin/linkedin-jobs" : "/employee/linkedin-jobs";
  const res = await axios.get(`${base}/alerts/${alertId}`);
  return res.data;
}

export interface ReassignedAlert {
  id: number;
  employee_id: number;
}

export async function reassignAlert(alertId: number, employeeId: number): Promise<ReassignedAlert> {
  const res = await axios.patch(`/admin/linkedin-jobs/alerts/${alertId}/reassign`, { employee_id: employeeId });
  return res.data;
}

export async function fetchTechManagers(): Promise<TechManager[]> {
  const res = await axios.get("/admin/linkedin-jobs/tech-managers");
  return res.data;
}

export interface EmployeeSlim {
  id: number;
  name: string;
}

export async function fetchAllEmployeesSlim(): Promise<EmployeeSlim[]> {
  const res = await axios.get("/admin/linkedin-jobs/all-employees");
  return res.data;
}

export async function deactivateJob(id: number): Promise<LinkedInJob> {
  const res = await axios.patch(`/admin/linkedin-jobs/${id}/deactivate`);
  return res.data;
}

export async function activateJob(id: number): Promise<LinkedInJob> {
  const res = await axios.patch(`/admin/linkedin-jobs/${id}/activate`);
  return res.data;
}

export interface CvDocumentSlim {
  id: number;
  title: string;
  category_id?: number | null;
}

export async function fetchEmployeeCvsForAdmin(employeeCode: string): Promise<CvDocumentSlim[]> {
  const res = await axios.get(`/admin/linkedin-jobs/employee-cvs/${encodeURIComponent(employeeCode)}`);
  return res.data;
}

export async function enhanceCvForJob(
  jobId: number,
  cvId: number,
  isAdmin: boolean,
  employeeCode?: string,
): Promise<{ alert_id: number; cv_id: number; enhanced_at: string; enhanced_cv_data: any; has_enhancement_logs: boolean }> {
  const base = isAdmin ? "/admin/linkedin-jobs" : "/employee/linkedin-jobs";
  const body = isAdmin ? { cv_id: cvId, employee_code: employeeCode } : { cv_id: cvId };
  const res = await axios.post(`${base}/${jobId}/enhance-cv`, body);
  return res.data;
}

export interface CoverLetterDocumentSlim {
  id: number;
  title: string;
  category_id?: number | null;
}

export async function fetchEmployeeCoverLettersForAdmin(employeeCode: string): Promise<CoverLetterDocumentSlim[]> {
  const res = await axios.get(`/admin/linkedin-jobs/employee-cover-letters/${encodeURIComponent(employeeCode)}`);
  return res.data;
}

export async function enhanceCoverLetterForJob(
  jobId: number,
  coverLetterId: number,
  isAdmin: boolean,
  employeeCode?: string,
  /** The CV currently selected in the sibling CvEnhancementSection's picker — passed
   * explicitly so grounding doesn't depend on the (much slower) CV enhancement pipeline
   * having already written its result back to the alert when both run concurrently via
   * "Enhance All". Falls back server-side to the alert's already-linked CV when omitted. */
  cvId?: number | null,
): Promise<{ alert_id: number; cover_letter_id: number; enhanced_at: string; enhanced_cover_letter_data: any; has_cover_letter_enhancement_logs: boolean }> {
  const base = isAdmin ? "/admin/linkedin-jobs" : "/employee/linkedin-jobs";
  const body = isAdmin
    ? { cover_letter_id: coverLetterId, employee_code: employeeCode, cv_id: cvId ?? null }
    : { cover_letter_id: coverLetterId, cv_id: cvId ?? null };
  const res = await axios.post(`${base}/${jobId}/enhance-cover-letter`, body);
  return res.data;
}

export interface ArchivedCleanupPreview {
  alert_count: number;
  jobs_to_fully_delete: number;
  jobs_to_keep: number;
}

export interface OldJobsCleanupPreview {
  job_count: number;
  alert_count: number;
  history_count: number;
  cutoff_iso: string;
}

export interface CleanupResult {
  deleted_alerts?: number;
  deleted_histories?: number;
  deleted_jobs?: number;
}

export async function fetchArchivedCleanupPreview(): Promise<ArchivedCleanupPreview> {
  const res = await axios.get("/admin/linkedin-jobs/cleanup/archived-preview");
  return res.data;
}

export async function executeArchivedCleanup(): Promise<CleanupResult> {
  const res = await axios.delete("/admin/linkedin-jobs/cleanup/archived");
  return res.data;
}

export async function fetchOldJobsPreview(months: number): Promise<OldJobsCleanupPreview> {
  const res = await axios.get("/admin/linkedin-jobs/cleanup/old-jobs-preview", { params: { months } });
  return res.data;
}

export async function executeOldJobsCleanup(months: number, cutoffIso?: string): Promise<CleanupResult> {
  const params: Record<string, string | number> = { months };
  if (cutoffIso) params.cutoff_iso = cutoffIso;
  const res = await axios.delete("/admin/linkedin-jobs/cleanup/old-jobs", { params });
  return res.data;
}

export async function fetchAlertCoverLetterEnhancementLogs(alertId: number, isAdmin: boolean): Promise<any[]> {
  const base = isAdmin ? "/admin/linkedin-jobs" : "/employee/linkedin-jobs";
  const res = await axios.get(`${base}/alerts/${alertId}/cover-letter-enhancement-logs`);
  return res.data.logs ?? [];
}

export async function fetchAlertEnhancementLogs(alertId: number, isAdmin: boolean): Promise<any[]> {
  const base = isAdmin ? "/admin/linkedin-jobs" : "/employee/linkedin-jobs";
  const res = await axios.get(`${base}/alerts/${alertId}/enhancement-logs`);
  return res.data.logs ?? [];
}

export interface FetchJobByUrlResult {
  status: "accepted" | "error";
  jobId?: string;
  message?: string;
}

export async function fetchJobByUrl(url: string, employeeCode: string): Promise<FetchJobByUrlResult> {
  const res = await axios.post("/admin/linkedin-jobs/fetch-by-url", { url, employee_code: employeeCode });
  return res.data;
}

export async function fetchJobByUrlAsEmployee(url: string): Promise<FetchJobByUrlResult> {
  const res = await axios.post("/employee/linkedin-jobs/fetch-by-url", { url });
  return res.data;
}
